import type { Feature, Geometry } from 'geojson';
import { mean } from 'lodash';
import { type FC, useMemo } from 'react';

import type { LiteConsolidatedComp } from '@/endpoints/itasca/comps/queryConsolidatedComps';
import { useSelectionsPanelContext } from '@/features/selectionsPanel/selectionsPanelProvider';
import type { IAcresCompFeature } from '@/models/features';
import { convertMoney } from '@/utils/common';
import { isDefined } from '@/utils/guards';

import { SoldLandCard } from '../selectionCard/cardVariants/soldLandCard';
import { PanelSectionDropdown } from './panelSectionDropdown';

export const SoldLandSectionDropdown: FC = () => {
  const { selectedComps } = useSelectionsPanelContext();

  const soldLandCount = useMemo(() => selectedComps.length, [selectedComps]);

  const selectionCards = useMemo(() => {
    return (
      <>
        {selectedComps.map((feature: IAcresCompFeature, idx) => (
          <SoldLandCard key={feature.id} feature={feature} idx={idx} />
        ))}
      </>
    );
  }, [selectedComps]);

  const averagePricePerAcre = useMemo(() => {
    const getCompPricePerAcre = (
      comp: IAcresCompFeature | Feature<Geometry, LiteConsolidatedComp>
    ) => {
      const { properties } = comp;
      let pricePerAcre: number | null = null;

      if ('price_per_acre_courthouse' in properties) {
        pricePerAcre = Number(properties.price_per_acre_courthouse) || null;
      }
      if ('price_per_acre' in properties && !pricePerAcre) {
        pricePerAcre = Number(properties.price_per_acre) || null;
      }
      if ('sale' in properties && !pricePerAcre) {
        pricePerAcre = Number(properties.sale.price_per_acre) || null;
      }

      return pricePerAcre;
    };

    return mean(selectedComps.map(getCompPricePerAcre).filter(isDefined));
  }, [selectedComps]);

  if (!soldLandCount) return null;

  return (
    <PanelSectionDropdown
      sectionCount={soldLandCount}
      subtitle={`${convertMoney(averagePricePerAcre)} - Mean PPA`}
      title="Sold Land"
    >
      {selectionCards}
    </PanelSectionDropdown>
  );
};
